import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface AICharacter {
  id: number;
  user_id: number;
  name: string;
  model_provider: 'claude' | 'gemini' | 'gpt' | 'grok';
  model_name: string;
  persona_info: any; // JSON object - may contain enabled_tools: string[]
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AICharacterFormData {
  name: string;
  model_provider: 'claude' | 'gemini' | 'gpt' | 'grok';
  model_name: string;
  persona_info: any; // JSON object or string - may contain enabled_tools: string[]
  is_active?: boolean;
  enabled_tools?: string[]; // Optional array of enabled tool names
}

export interface CreateAICharacterData {
  user_id: string;
  name: string;
  model_provider: 'claude' | 'gemini' | 'gpt' | 'grok';
  model_name: string;
  persona_info: any; // JSON object
  is_active?: boolean;
}

export interface UpdateAICharacterData {
  id: string;
  name?: string;
  model_provider?: 'claude' | 'gemini' | 'gpt' | 'grok';
  model_name?: string;
  persona_info?: any; // JSON object or string
  is_active?: boolean;
}

export interface AICharacterFilters {
  user_id: string;
  search_term?: string;
  model_provider?: 'claude' | 'gemini' | 'gpt' | 'grok';
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

class AICharacterService {
  // Create a new AI character
  async createCharacter(userId: string, characterData: AICharacterFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      // Ensure persona_info is a JSON object
      let personaInfo = characterData.persona_info;
      if (typeof personaInfo === 'string') {
        try {
          personaInfo = JSON.parse(personaInfo);
        } catch (error) {
          // If parsing fails, wrap it in an object
          personaInfo = { description: personaInfo };
        }
      }

      const data: CreateAICharacterData = {
        user_id: userId,
        name: characterData.name,
        model_provider: characterData.model_provider,
        model_name: characterData.model_name,
        persona_info: personaInfo,
        is_active: characterData.is_active !== undefined ? characterData.is_active : true
      };

      const response = await makeAuthenticatedRequest('/ai-characters', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating AI character:', error);
      throw error;
    }
  }

  // Get AI character by ID
  async getCharacterById(characterId: string): Promise<{ success: boolean; character?: AICharacter }> {
    try {
      const response = await makeAuthenticatedRequest(`/ai-characters/${characterId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching AI character:', error);
      throw error;
    }
  }

  // Get all AI characters for a user
  async getUserCharacters(filters: AICharacterFilters): Promise<{ success: boolean; characters?: AICharacter[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.model_provider) queryParams.append('model_provider', filters.model_provider);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/ai-characters/user/${filters.user_id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user AI characters:', error);
      throw error;
    }
  }

  // Update an AI character
  async updateCharacter(updateData: UpdateAICharacterData): Promise<{ success: boolean; message?: string }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      // Ensure persona_info is a JSON object if provided
      if (dataToUpdate.persona_info !== undefined) {
        if (typeof dataToUpdate.persona_info === 'string') {
          try {
            dataToUpdate.persona_info = JSON.parse(dataToUpdate.persona_info);
          } catch (error) {
            // If parsing fails, wrap it in an object
            dataToUpdate.persona_info = { description: dataToUpdate.persona_info };
          }
        }
      }
      
      const response = await makeAuthenticatedRequest(`/ai-characters/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating AI character:', error);
      throw error;
    }
  }

  // Delete an AI character
  async deleteCharacter(characterId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/ai-characters/${characterId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting AI character:', error);
      throw error;
    }
  }

  // Search AI characters
  async searchCharacters(filters: AICharacterFilters): Promise<{ success: boolean; characters?: AICharacter[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', filters.user_id);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.model_provider) queryParams.append('model_provider', filters.model_provider);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/ai-characters/search?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error searching AI characters:', error);
      throw error;
    }
  }
}

export const aiCharacterService = new AICharacterService();

